import { match } from 'ts-pattern'

import type { ConductorTask } from 'Automations/nodeDefinitions/conductor'
import {
  MAX_PATHS,
  OPTIONAL_DEPENDENCY_EXECUTORS,
  QUOTE_APPROVAL_TERMINAL_MATCHERS,
  TERMINAL_PRECEDENCE
} from '../../simulation.constants'
import type {
  AutomationAnalysis,
  SimulationExpression,
  SimulationPath,
  SimulationWorkflow,
  TerminalTaskMatcher,
  TerminalType
} from '../../simulation.types'
import { comparePathPriority } from '../comparePathPriority'
import {
  collectPathDependencies,
  dedupeDependencies
} from './collectPathDependencies'
import { matchTerminalType } from './matchTerminalType'
import { switchBranches } from './switchBranches'

/**
 * Works out every route a workflow can take to an end state, which switch
 * branches have to be taken to get there, and what data those branches read.
 *
 * Returns undefined when there are more routes than the cap allows, which means
 * we can't say anything useful about the workflow and the caller should fall
 * back to whatever it does without us.
 */
export const analyseAutomation = ({
  workflow,
  terminalMatchers = QUOTE_APPROVAL_TERMINAL_MATCHERS,
  terminalPrecedence = TERMINAL_PRECEDENCE,
  optionalDependencyExecutors = OPTIONAL_DEPENDENCY_EXECUTORS
}: {
  workflow: SimulationWorkflow
  terminalMatchers?: TerminalTaskMatcher[]
  terminalPrecedence?: TerminalType[]
  /** Executors allowed to answer with nothing, marked on each dependency. */
  optionalDependencyExecutors?: string[]
}): AutomationAnalysis | undefined => {
  const walked = walkTasks({
    tasks: workflow.tasks,
    expressions: [],
    terminalMatchers,
    terminalPrecedence,
    maxPaths: MAX_PATHS
  })

  if (walked.length > MAX_PATHS) return undefined

  const paths = walked
    .map(path => ({
      ...path,
      ...collectPathDependencies({
        expressions: path.expressions,
        workflow,
        optionalDependencyExecutors
      })
    }))
    .sort(comparePathPriority)

  return {
    paths,
    dependencies: dedupeDependencies(paths.flatMap(path => path.dependencies))
  }
}

type WalkedPath = Pick<
  SimulationPath,
  'expressions' | 'terminalType' | 'terminalTask'
>

type Walk = {
  tasks: ConductorTask[]
  expressions: SimulationExpression[]
  terminalMatchers: TerminalTaskMatcher[]
  terminalPrecedence: TerminalType[]
  maxPaths: number
}

/**
 * Walks a list of tasks in order and returns the routes that reach an end
 * state.
 *
 * A branch that reaches none is dropped. Conductor just completes the workflow
 * when it runs off the end of a branch, and that isn't an outcome worth telling
 * anyone about.
 */
const walkTasks = ({ tasks, ...walk }: Walk): WalkedPath[] => {
  const [task, ...rest] = tasks
  if (!task) return []

  const terminalType = matchTerminalType({
    task,
    terminalMatchers: walk.terminalMatchers
  })
  if (terminalType) {
    return [{ expressions: walk.expressions, terminalType, terminalTask: task }]
  }

  return (
    match(task)
      .with({ type: 'SWITCH' }, switchTask =>
        walkAllTasksInBranches({
          walk,
          branches: switchBranches(switchTask).map(branch => ({
            tasks: [...branch.tasks, ...rest],
            expressions: [...walk.expressions, branch.expression]
          }))
        })
      )
      .with({ type: 'FORK_JOIN' }, forkJoinTask => {
        const branchPaths = walkAllTasksInBranches({
          walk,
          branches: forkJoinTask.forkTasks.map(branchTasks => ({
            tasks: branchTasks,
            expressions: walk.expressions
          }))
        })

        return branchPaths.length > 0
          ? findForkTerminalTasks({
              paths: branchPaths,
              terminalPrecedence: walk.terminalPrecedence
            })
          : walkTasks({ ...walk, tasks: rest })
      })
      // A loop body always runs at least once, and an end state inside it
      // finishes the workflow on that first pass, so walking it once is enough.
      .with({ type: 'DO_WHILE' }, doWhileTask =>
        walkTasks({ ...walk, tasks: [...doWhileTask.loopOver, ...rest] })
      )
      .otherwise(() => walkTasks({ ...walk, tasks: rest }))
  )
}

/**
 * Walks every branch of a switch or a fork, and gathers up the routes they
 * reach.
 *
 * We stop early once there are more routes than we're willing to look at. Every
 * switch doubles the number of them, so on a broken workflow this would never
 * finish on its own.
 */
const walkAllTasksInBranches = ({
  walk,
  branches
}: {
  walk: Omit<Walk, 'tasks'>
  branches: Array<Pick<Walk, 'tasks' | 'expressions'>>
}): WalkedPath[] =>
  branches.reduce<WalkedPath[]>(
    (acc, branch) =>
      acc.length > walk.maxPaths
        ? acc
        : [...acc, ...walkTasks({ ...walk, ...branch })],
    []
  )

/**
 * Everything in a fork runs at once, so if one branch ends in a review then the
 * quote gets reviewed, whatever the other branches do. We report the most
 * restrictive end state any of them reach.
 *
 * Each branch keeps its own conditions as a separate route, they just all agree
 * on the outcome.
 *
 *   branch A  (no conditions)  -> PUBLISH  --+
 *   branch B  [group X]        -> REVIEW  ---+-> both routes report REVIEW
 */
const findForkTerminalTasks = ({
  paths,
  terminalPrecedence
}: {
  paths: WalkedPath[]
  terminalPrecedence: TerminalType[]
}): WalkedPath[] => {
  const highestPrecedentTerminalTask = paths.reduce<WalkedPath | undefined>(
    (currentHighest, path) => {
      if (!currentHighest) return path

      // Precedence is an index into `terminalPrecedence`, which is ordered
      // most-restrictive-first, so a lower number wins.
      const isMoreRestrictive =
        precedenceOf({ path, terminalPrecedence }) <
        precedenceOf({ path: currentHighest, terminalPrecedence })

      return isMoreRestrictive ? path : currentHighest
    },
    undefined
  )
  if (!highestPrecedentTerminalTask) return []

  const deduped = paths.reduce<Record<string, WalkedPath>>((acc, path) => {
    const key = conditionsKey(path)
    // Branches reaching the same end state under the same conditions are one
    // route, so the first one we met stands for all of them.
    if (acc[key]) return acc

    return {
      ...acc,
      [key]: {
        expressions: path.expressions,
        terminalType: highestPrecedentTerminalTask.terminalType,
        terminalTask: highestPrecedentTerminalTask.terminalTask
      }
    }
  }, {})

  return Object.values(deduped)
}

const conditionsKey = (path: WalkedPath): string =>
  JSON.stringify(
    path.expressions.map(expression => [
      expression.switchTaskReferenceName,
      expression.result
    ])
  )

const precedenceOf = ({
  path,
  terminalPrecedence
}: {
  path: WalkedPath
  terminalPrecedence: TerminalType[]
}): number => {
  const index = terminalPrecedence.indexOf(path.terminalType)
  return index === -1 ? terminalPrecedence.length : index
}
